import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { storeRevenueCategoryData } from "@/Api/revenueCategories";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ApiDataProps } from "@/pages/Tenants";
import { RevenueCategoryProps } from "@/types/revenueCategory";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  description: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const AddRevenueCategoryBtn = ({
  updateFront,
}: {
  updateFront: React.Dispatch<React.SetStateAction<ApiDataProps<RevenueCategoryProps>>>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const payload = {
      name: data.name,
      description: data.description || "",
    };

    try {
      const { data: apiResponse, ok } = await storeRevenueCategoryData(payload);
      if (!ok) {
        setFormState("error");
        setTimeout(() => {
          setFormState("default");
        }, 1000);
        return;
      }

      updateFront((prev) => ({
        ...prev,
        data: [...prev.data, apiResponse.response],
      }));

      toast({
        description: apiResponse?.message || "Categoria criada com sucesso!",
        action: <Check color="#1edc2b" />,
      });

      form.reset({
        name: "",
        description: "",
      });
      
      setFormState("default");
      setIsOpen(false);
    } catch (error) {
      console.error("Erro ao adicionar categoria:", error);
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
      }, 1000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Categoria
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Categoria
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações da Categoria de Receita</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh]">
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-3"
          >
            <Form.Row>
              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Nome da categoria" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            
            <Form.Row>
              <Form.Field name="description">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Descrição <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Textarea
                        placeholder="Descrição da categoria"
                        className="resize-none"
                        {...field}
                        value={field.value || ""}
                      />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <DialogFooter>
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Adicionando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Adicionar"}
                {formState === "error" && "Erro ao adicionar"}
              </Button>
            </DialogFooter>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};